import { api } from './client';
import {
  vehicleFromApi, vehicleToApi,
  customerFromApi, customerToApi,
  rentalFromApi, rentalToApi,
  maintFromApi, maintToApi,
  invoiceFromApi, invoiceToApi,
  damageFromApi, damageToApi,
  refundFromApi,
  dateChangeRequestFromApi,
  notificationFromApi,
  roleFromApi,
  userFromApi,
  locationFromApi, locationToApi,
  areaFromApi,
  groupFromApi,
  vehicleTypeFromApi,
  rateRuleFromApi,
  promoCodeFromApi,
  extraFromApi,
  insurancePlanFromApi,
  poiFromApi,
  agreementTemplateFromApi,
  companySettingsFromApi,
} from './mappers';

const list = (fn) => (r) => (r || []).map(fn);

// ── Auth ─────────────────────────────────────────────────────────────────────
export const authService = {
  login:  (email, password) => api.post('/auth/login', { email, password, device_name: 'crm-web' }),
  me:     ()                => api.get('/auth/me'),
  logout: ()                => api.post('/auth/logout'),
};

// ── Vehicles ──────────────────────────────────────────────────────────────────
export const vehicleService = {
  list:   ()            => api.get('/vehicles').then(list(vehicleFromApi)),
  create: (data)        => api.post('/vehicles', vehicleToApi(data)).then(vehicleFromApi),
  update: (id, data)    => api.put(`/vehicles/${id}`, vehicleToApi(data)).then(vehicleFromApi),
  delete: (id)          => api.delete(`/vehicles/${id}`),
};

// ── Customers ─────────────────────────────────────────────────────────────────
export const customerService = {
  list:   ()            => api.get('/customers').then(list(customerFromApi)),
  create: (data)        => api.post('/customers', customerToApi(data)).then(customerFromApi),
  update: (id, data)    => api.put(`/customers/${id}`, customerToApi(data)).then(customerFromApi),
};

// ── Rentals ───────────────────────────────────────────────────────────────────
export const rentalService = {
  list:      ()            => api.get('/rentals').then(list(rentalFromApi)),
  history:   ()            => api.get('/rentals/history').then(list(rentalFromApi)),
  create:    (data)        => api.post('/rentals', rentalToApi(data)).then(rentalFromApi),
  update:    (id, data)    => api.put(`/rentals/${id}`, rentalToApi(data)).then(rentalFromApi),
  checkIn:   (id, data)    => api.patch(`/rentals/${id}/check-in`, data).then(rentalFromApi),
  checkOut:  (id, data)    => api.patch(`/rentals/${id}/check-out`, data).then(rentalFromApi),
  sign:      (id, data)    => api.post(`/rentals/${id}/sign`, data).then(rentalFromApi),
  extend:    (id, data)    => api.post(`/rentals/${id}/extend`, data),
  endLease:  (id, data)    => api.post(`/rentals/${id}/end-lease`, data).then(r => rentalFromApi(r?.rental || r)),
  assign:    (id, data)    => api.patch(`/rentals/${id}/assign`, data),
  unassign:  (id)          => api.patch(`/rentals/${id}/unassign`),
};

// ── Incoming bookings ─────────────────────────────────────────────────────────
export const bookingService = {
  list:    ()         => api.get('/incoming-bookings').then(list(rentalFromApi)),
  confirm: (id)       => api.patch(`/incoming-bookings/${id}/confirm`).then(rentalFromApi),
  decline: (id, data) => api.patch(`/incoming-bookings/${id}/decline`, data),
};

// ── Invoices ──────────────────────────────────────────────────────────────────
export const invoiceService = {
  list:   ()            => api.get('/invoices').then(list(invoiceFromApi)),
  create: (data)        => api.post('/invoices', invoiceToApi(data)).then(invoiceFromApi),
  updateStatus: (id, status, extra) => api.patch(`/invoices/${id}/status`, { status, ...extra }),
};

// ── Damages ───────────────────────────────────────────────────────────────────
export const damageService = {
  list:   ()            => api.get('/damages').then(list(damageFromApi)),
  create: (data)        => api.post('/damages', damageToApi(data)).then(damageFromApi),
  update: (id, data)    => api.put(`/damages/${id}`, damageToApi(data)).then(damageFromApi),
  delete: (id)          => api.delete(`/damages/${id}`),
};

// ── Refunds ───────────────────────────────────────────────────────────────────
export const refundService = {
  list:    ()            => api.get('/refunds').then(list(refundFromApi)),
  create:  (data)        => api.post('/refunds', data).then(refundFromApi),
  process: (id, data)    => api.post(`/refunds/${id}/process`, data).then(refundFromApi),
  decline: (id, data)    => api.post(`/refunds/${id}/decline`, data).then(refundFromApi),
};

// ── Date-change requests ──────────────────────────────────────────────────────
export const dateChangeService = {
  list:   ()            => api.get('/date-change-requests').then(list(dateChangeRequestFromApi)),
  accept: (id, data)    => api.post(`/date-change-requests/${id}/accept`, data),
  refuse: (id)          => api.post(`/date-change-requests/${id}/refuse`),
};

// ── Maintenance ───────────────────────────────────────────────────────────────
export const maintService = {
  list:     ()            => api.get('/maintenance-records').then(list(maintFromApi)),
  create:   (data)        => api.post('/maintenance-records', maintToApi(data)).then(maintFromApi),
  update:   (id, data)    => api.put(`/maintenance-records/${id}`, maintToApi(data)).then(maintFromApi),
  complete: (id, data)    => api.patch(`/maintenance-records/${id}/complete`, maintToApi(data)).then(maintFromApi),
};

// ── Notifications ─────────────────────────────────────────────────────────────
export const notifService = {
  list:        ()    => api.get('/notifications').then(list(notificationFromApi)),
  markRead:    (id)  => api.patch(`/notifications/${id}`),
  markAllRead: ()    => api.patch('/notifications/read-all'),
};

// ── Roles ─────────────────────────────────────────────────────────────────────
export const roleService = {
  list:   ()            => api.get('/roles').then(list(roleFromApi)),
  create: (data)        => api.post('/roles', data).then(roleFromApi),
  update: (id, data)    => api.put(`/roles/${id}`, data).then(roleFromApi),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const userService = {
  list:   ()            => api.get('/users').then(list(userFromApi)),
  create: (data)        => api.post('/users', data).then(userFromApi),
  update: (id, data)    => api.put(`/users/${id}`, data).then(userFromApi),
};

// ── Catalog ───────────────────────────────────────────────────────────────────
export const areaService = {
  list:   ()            => api.get('/areas').then(list(areaFromApi)),
  create: (data)        => api.post('/areas', data).then(areaFromApi),
  update: (id, data)    => api.put(`/areas/${id}`, data).then(areaFromApi),
  delete: (id)          => api.delete(`/areas/${id}`),
};

export const locationService = {
  list:   ()            => api.get('/locations').then(list(locationFromApi)),
  create: (data)        => api.post('/locations', locationToApi(data)).then(locationFromApi),
  update: (id, data)    => api.put(`/locations/${id}`, locationToApi(data)).then(locationFromApi),
  delete: (id)          => api.delete(`/locations/${id}`),
};

export const groupService = {
  list:   ()            => api.get('/vehicle-groups').then(list(groupFromApi)),
  create: (data)        => api.post('/vehicle-groups', data).then(groupFromApi),
  update: (id, data)    => api.put(`/vehicle-groups/${id}`, data).then(groupFromApi),
  delete: (id)          => api.delete(`/vehicle-groups/${id}`),
};

export const vehicleTypeService = {
  list:   ()            => api.get('/vehicle-types').then(list(vehicleTypeFromApi)),
  create: (data)        => api.post('/vehicle-types', data).then(vehicleTypeFromApi),
  update: (id, data)    => api.put(`/vehicle-types/${id}`, data).then(vehicleTypeFromApi),
  delete: (id)          => api.delete(`/vehicle-types/${id}`),
};

export const rateRuleService = {
  list:   ()            => api.get('/rate-rules').then(list(rateRuleFromApi)),
  create: (data)        => api.post('/rate-rules', data).then(rateRuleFromApi),
  update: (id, data)    => api.put(`/rate-rules/${id}`, data).then(rateRuleFromApi),
  delete: (id)          => api.delete(`/rate-rules/${id}`),
};

export const promoCodeService = {
  list:   ()            => api.get('/promo-codes').then(list(promoCodeFromApi)),
  create: (data)        => api.post('/promo-codes', data).then(promoCodeFromApi),
  update: (id, data)    => api.put(`/promo-codes/${id}`, data).then(promoCodeFromApi),
  delete: (id)          => api.delete(`/promo-codes/${id}`),
};

export const extraService = {
  list:   ()            => api.get('/extras').then(list(extraFromApi)),
  create: (data)        => api.post('/extras', data).then(extraFromApi),
  update: (id, data)    => api.put(`/extras/${id}`, data).then(extraFromApi),
  delete: (id)          => api.delete(`/extras/${id}`),
};

export const insuranceService = {
  list:   ()            => api.get('/insurance-plans').then(list(insurancePlanFromApi)),
  create: (data)        => api.post('/insurance-plans', data).then(insurancePlanFromApi),
  update: (id, data)    => api.put(`/insurance-plans/${id}`, data).then(insurancePlanFromApi),
  delete: (id)          => api.delete(`/insurance-plans/${id}`),
};

export const poiService = {
  list:   ()            => api.get('/pois').then(list(poiFromApi)),
  create: (data)        => api.post('/pois', data).then(poiFromApi),
  update: (id, data)    => api.put(`/pois/${id}`, data).then(poiFromApi),
  delete: (id)          => api.delete(`/pois/${id}`),
};

export const agreementService = {
  list:   ()            => api.get('/agreement-templates').then(list(agreementTemplateFromApi)),
  create: (data)        => api.post('/agreement-templates', data).then(agreementTemplateFromApi),
  update: (id, data)    => api.put(`/agreement-templates/${id}`, data).then(agreementTemplateFromApi),
  delete: (id)          => api.delete(`/agreement-templates/${id}`),
};

// ── Company settings ──────────────────────────────────────────────────────────
export const settingsService = {
  get:    ()     => api.get('/company/settings').then(companySettingsFromApi),
  update: (data) => api.put('/company/settings', data).then(companySettingsFromApi),
};

// ── Revenue ───────────────────────────────────────────────────────────────────
export const revenueService = {
  summary: (params) => {
    const qs = new URLSearchParams(params || {}).toString();
    return api.get('/revenue/summary' + (qs ? '?' + qs : ''));
  },
  export: (params) => {
    const qs = new URLSearchParams(params || {}).toString();
    return api.get('/revenue/export' + (qs ? '?' + qs : ''));
  },
};

// ── File uploads ──────────────────────────────────────────────────────────────
export const uploadService = {
  upload:  (formData) => {
    const token = localStorage.getItem('auth_token');
    return fetch((process.env.REACT_APP_API_URL || '/api') + '/uploads', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    }).then(r => r.json());
  },
  delete: (key) => api.delete(`/uploads/${encodeURIComponent(key)}`),
};
